import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import IconButton from "@mui/material/IconButton";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import RegisterUserForm from "./Register";
import { useEffect, useState } from "react";
import LoginUserForm from "./Login";
import ForgotPasswordForm from "./ForgotPassword";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import useSiteSettings from "../../../hooks/useSiteSettings";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "92%", md: 860 },
  maxWidth: 860,
  maxHeight: "94vh",
  bgcolor: "#fff",
  borderRadius: "22px",
  boxShadow: 24,
  overflow: "hidden",
  p: 0,
};

export default function AuthModal({ handleClose, open }) {
  const { auth } = useSelector((store) => store);
  const [pageType, setPageType] = useState("login");
  const settings = useSiteSettings();
  const brand = settings.brandName || "";
  const navigate = useNavigate();

  useEffect(() => {
    if (!auth.user) return;

    // `open` scopes this to a sign-in that just happened in this modal.
    // auth.user is also populated on every page load for an already-signed-in
    // visitor, and without the guard the redirect below would fire then too —
    // yanking an admin to the dashboard every time they opened a storefront
    // page, which would make the shop unbrowsable for them.
    if (!open) return;

    handleClose();

    // App.js only redirects admins whose path is exactly "/", so signing in
    // from any other page used to leave them on the storefront until they
    // refreshed. Send them to the dashboard from wherever they signed in.
    if (auth.user.role === "ADMIN") {
      navigate("/admin");
    }
  }, [auth.user, open, handleClose, navigate]);

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={style}>
        {/* max-h matches the Box cap so the row can never exceed it */}
        <div className="flex max-h-[94vh]">
          {/* Brand panel */}
          <div className="relative hidden w-[44%] flex-col justify-between bg-gradient-to-br from-[#5e3e3e] to-[#2a1810] p-8 text-[#f1e7da] md:flex">
            <div
              className="absolute inset-0 opacity-[0.08]"
              style={{
                backgroundImage:
                  "radial-gradient(circle at 20% 20%, #ffffff 1.2px, transparent 1.2px)",
                backgroundSize: "16px 16px",
              }}
            />
            <div className="relative z-10">
              {brand && (
                <h2 className="font-display text-3xl font-bold leading-tight text-[#f1e7da]">
                  {brand}
                </h2>
              )}
              <p className="mt-2 text-[11px] uppercase tracking-[0.22em] text-[#e0b65c]">
                Royal Wedding Attire
              </p>
            </div>
            <div className="relative z-10">
              <p className="font-display text-2xl leading-snug text-[#f1e7da]">
                “Dress like royalty on your big day.”
              </p>
              <p className="mt-3 text-sm leading-relaxed text-[#f1e7da]/70">
                Handcrafted sherwanis, regal safas &amp; statement kalgis — made
                with love in Rajasthan.
              </p>
            </div>
            <p className="relative z-10 text-xs text-[#f1e7da]/45">
              © {new Date().getFullYear()}
              {brand ? ` ${brand}` : ""}
            </p>
          </div>

          {/* Form panel */}
          <div className="relative w-full md:w-[56%]">
            {/* Outside the scrolling area, so it stays reachable however far the
                form is scrolled. Sits above the content on its own layer. */}
            <IconButton aria-label="Close"
              onClick={handleClose}
              sx={{
                position: "absolute",
                top: 10,
                right: 10,
                zIndex: 1,
                color: "#9a7575",
                bgcolor: "rgba(255,255,255,0.85)",
                backdropFilter: "blur(4px)",
                "&:hover": { bgcolor: "rgba(255,255,255,0.95)" },
              }}
            >
              <CloseRoundedIcon />
            </IconButton>
            {/* The Box is maxHeight:94vh with overflow:hidden, so the sign-up
                form (six fields) was clipped on short screens with no way to
                reach the button. Scroll it here instead; padding lives inside so
                it scrolls with the content. */}
            <div className="max-h-[94vh] overflow-y-auto overscroll-contain p-6 sm:p-8">
              {pageType === "login" && <LoginUserForm setPageType={setPageType} />}
              {pageType === "register" && <RegisterUserForm setPageType={setPageType} />}
              {pageType === "forgot" && <ForgotPasswordForm setPageType={setPageType} />}
            </div>
          </div>
        </div>
      </Box>
    </Modal>
  );
}
